import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState, type ReactNode } from 'react'
import {
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  Power,
  PowerOff,
  TableProperties,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { PlanForm } from '@/features/super-admin/components/plans/PlanForm'
import { ConfirmModal } from '@/features/super-admin/components/ConfirmModal'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import {
  type Plan,
  useCreatePlan,
  useDeactivatePlan,
  usePlans,
  useReactivatePlan,
  useUpdatePlan,
} from '@/features/super-admin/hooks/use-plans'

export const Route = createFileRoute('/super-admin/plans/')({
  component: PlansPage,
})

function PlansPage() {
  const { onMenuToggle } = useMenuToggle()
  const [showInactive, setShowInactive] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null)
  const [deactivatingPlan, setDeactivatingPlan] = useState<Plan | null>(null)

  const { data, isLoading } = usePlans({ includeInactive: showInactive })
  const createMutation = useCreatePlan()
  const updateMutation = useUpdatePlan()
  const deactivateMutation = useDeactivatePlan()
  const reactivateMutation = useReactivatePlan()

  const plans = data?.data || []
  const activePlans = plans.filter((plan) => plan.is_active)
  const fixedPlans = activePlans.filter((plan) => plan.billing_model !== 'custom')

  const startingPrice = useMemo(() => {
    const prices = fixedPlans
      .map((plan) => plan.price_monthly_cents)
      .filter((price) => price > 0)
      .sort((a, b) => a - b)
    return prices[0] || 0
  }, [fixedPlans])

  const handleCreate = async (formData: Parameters<typeof createMutation.mutate>[0]) => {
    try {
      await createMutation.mutateAsync(formData)
      setShowForm(false)
    } catch (error) {
      console.error('Failed to create plan:', error)
    }
  }

  const handleUpdate = async (formData: Parameters<typeof updateMutation.mutate>[0]['data']) => {
    if (!editingPlan) return
    try {
      await updateMutation.mutateAsync({ id: editingPlan.id, data: formData })
      setEditingPlan(null)
    } catch (error) {
      console.error('Failed to update plan:', error)
    }
  }

  const confirmDeactivate = async () => {
    if (!deactivatingPlan) return
    try {
      await deactivateMutation.mutateAsync(deactivatingPlan.id)
    } catch (error) {
      console.error('Failed to deactivate plan:', error)
    } finally {
      setDeactivatingPlan(null)
    }
  }

  const handleReactivate = async (plan: Plan) => {
    try {
      await reactivateMutation.mutateAsync(plan.id)
    } catch (error) {
      console.error('Failed to reactivate plan:', error)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <SuperAdminHeader
        title="Planos"
        subtitle="Estrutura comercial, preços e limites por plano"
        onMenuToggle={onMenuToggle}
        actions={
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowInactive((value) => !value)}
              aria-label={showInactive ? 'Ocultar inativos' : 'Mostrar inativos'}
              title={showInactive ? 'Ocultar inativos' : 'Mostrar inativos'}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                showInactive
                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {showInactive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              <span className="hidden sm:inline">{showInactive ? 'Ocultar inativos' : 'Mostrar inativos'}</span>
            </button>
            <button
              onClick={() => setShowForm(true)}
              aria-label="Novo plano"
              title="Novo plano"
              className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-700"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Novo plano</span>
            </button>
          </div>
        }
      />

      <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col gap-5 overflow-auto p-4 md:p-5">
        <section className="grid gap-4 md:grid-cols-3">
          <SummaryCard
            icon={TableProperties}
            label="Planos ativos"
            value={activePlans.length.toLocaleString('pt-BR')}
            detail="Modelo atual com todos os recursos inclusos"
          />
          <SummaryCard
            icon={CheckCircle2}
            label="Preço de entrada"
            value={startingPrice ? formatPrice(startingPrice) : 'Sob consulta'}
            detail="Plano básico começa a partir deste valor"
          />
          <SummaryCard
            icon={Users}
            label="Assinaturas ativas"
            value={plans
              .reduce((total, plan) => total + (plan.subscriber_count || 0), 0)
              .toLocaleString('pt-BR')}
            detail="Organizações vinculadas aos planos"
          />
        </section>

        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-2 border-b border-gray-200 px-5 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Grade operacional de planos</h2>
              <p className="mt-1 text-sm text-gray-500">
                As funcionalidades são iguais em todos. A diferenciação fica em conta, unidades,
                profissionais e pacientes.
              </p>
            </div>
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Recursos liberados em todos os planos
            </span>
          </div>

          <PlanGrid
            plans={plans}
            loading={isLoading}
            onEdit={setEditingPlan}
            onDeactivate={setDeactivatingPlan}
            onReactivate={handleReactivate}
          />
        </section>
      </div>

      {showForm && (
        <PlanForm
          mode="create"
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          isLoading={createMutation.isPending}
        />
      )}

      {editingPlan && (
        <PlanForm
          mode="edit"
          plan={editingPlan}
          onSubmit={handleUpdate}
          onCancel={() => setEditingPlan(null)}
          isLoading={updateMutation.isPending}
        />
      )}

      <ConfirmModal
        isOpen={!!deactivatingPlan}
        onClose={() => setDeactivatingPlan(null)}
        onConfirm={confirmDeactivate}
        title="Desativar plano"
        message={`Deseja desativar "${deactivatingPlan?.name}"? Ele deixa de aparecer para novas organizações.`}
        confirmText="Desativar"
        variant="danger"
        isLoading={deactivateMutation.isPending}
      />
    </div>
  )
}

function PlanGrid({
  plans,
  loading,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  plans: Plan[]
  loading: boolean
  onEdit: (plan: Plan) => void
  onDeactivate: (plan: Plan) => void
  onReactivate: (plan: Plan) => void
}) {
  if (loading) {
    return (
      <div className="space-y-3 p-5">
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="h-16 animate-pulse rounded-lg bg-gray-100" />
        ))}
      </div>
    )
  }

  if (plans.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <TableProperties className="h-6 w-6 text-gray-400" />
        </div>
        <h3 className="font-medium text-gray-900">Nenhum plano encontrado</h3>
        <p className="mt-1 text-sm text-gray-500">Crie o primeiro plano para estruturar a oferta.</p>
      </div>
    )
  }

  return (
    <>
      <div className="divide-y divide-gray-100 xl:hidden">
        {plans.map((plan) => (
          <CompactPlanRow
            key={plan.id}
            plan={plan}
            onEdit={onEdit}
            onDeactivate={onDeactivate}
            onReactivate={onReactivate}
          />
        ))}
      </div>

      <div className="hidden overflow-x-auto xl:block">
        <table className="w-full min-w-[980px]">
        <thead className="bg-gray-50">
          <tr className="border-b border-gray-200">
            <HeaderCell>Plano</HeaderCell>
            <HeaderCell>Preço</HeaderCell>
            <HeaderCell>Conta</HeaderCell>
            <HeaderCell>Unidades</HeaderCell>
            <HeaderCell>Profissionais</HeaderCell>
            <HeaderCell>Pacientes</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell className="text-right">Ações</HeaderCell>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {plans.map((plan) => (
            <tr key={plan.id} className="transition-colors hover:bg-gray-50">
              <td className="px-5 py-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      plan.is_highlighted ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{plan.name}</p>
                      {plan.badge_text && (
                        <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700">
                          {plan.badge_text}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500">{plan.slug}</p>
                  </div>
                </div>
              </td>
              <td className="px-5 py-4">
                <p className="text-sm font-semibold text-gray-900">
                  {plan.billing_model === 'custom' ? 'Sob consulta' : `${formatPrice(plan.price_monthly_cents)}/mês`}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {plan.billing_model === 'custom' ? 'Contrato customizado' : `${formatPrice(plan.price_yearly_cents || 0)}/ano`}
                </p>
              </td>
              <td className="px-5 py-4">
                <span className="text-sm text-gray-700">{plan.account_scope}</span>
              </td>
              <td className="px-5 py-4">
                <LimitValue value={plan.max_units} unlimited={plan.billing_model === 'custom'} />
              </td>
              <td className="px-5 py-4">
                <LimitValue value={plan.max_professionals} unlimited={plan.billing_model === 'custom'} />
              </td>
              <td className="px-5 py-4">
                <LimitValue value={plan.max_patients} unlimited={plan.billing_model === 'custom'} />
              </td>
              <td className="px-5 py-4">
                <div className="flex flex-wrap gap-2">
                  <StatusBadge active={plan.is_active} />
                  {!plan.is_public && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      Privado
                    </span>
                  )}
                  {plan.subscriber_count > 0 && (
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                      {plan.subscriber_count} cliente{plan.subscriber_count > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-5 py-4 text-right">
                <PlanActions
                  plan={plan}
                  onEdit={onEdit}
                  onDeactivate={onDeactivate}
                  onReactivate={onReactivate}
                />
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    </>
  )
}

function CompactPlanRow({
  plan,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  plan: Plan
  onEdit: (plan: Plan) => void
  onDeactivate: (plan: Plan) => void
  onReactivate: (plan: Plan) => void
}) {
  return (
    <div className="p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
              plan.is_highlighted ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'
            }`}
          >
            <Building2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium text-gray-900">{plan.name}</p>
              {plan.badge_text && (
                <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700">
                  {plan.badge_text}
                </span>
              )}
              <StatusBadge active={plan.is_active} />
            </div>
            <p className="mt-0.5 text-xs text-gray-500">{plan.slug}</p>
          </div>
        </div>
        <PlanActions
          plan={plan}
          onEdit={onEdit}
          onDeactivate={onDeactivate}
          onReactivate={onReactivate}
        />
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <CompactMetric
          label="Preço"
          value={
            plan.billing_model === 'custom'
              ? 'Sob consulta'
              : `${formatPrice(plan.price_monthly_cents)}/mês`
          }
          detail={plan.billing_model === 'custom' ? 'Contrato customizado' : `${formatPrice(plan.price_yearly_cents || 0)}/ano`}
        />
        <CompactMetric label="Conta" value={plan.account_scope} />
        <CompactMetric
          label="Unidades"
          value={formatLimit(plan.max_units, plan.billing_model === 'custom')}
        />
        <CompactMetric
          label="Profissionais / Pacientes"
          value={`${formatLimit(plan.max_professionals, plan.billing_model === 'custom')} / ${formatLimit(plan.max_patients, plan.billing_model === 'custom')}`}
        />
      </div>
    </div>
  )
}

function CompactMetric({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-sm font-semibold text-gray-900">{value}</p>
      {detail && <p className="mt-0.5 text-xs text-gray-500">{detail}</p>}
    </div>
  )
}

function PlanActions({
  plan,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  plan: Plan
  onEdit: (plan: Plan) => void
  onDeactivate: (plan: Plan) => void
  onReactivate: (plan: Plan) => void
}) {
  return (
    <div className="flex justify-end gap-2">
      <button
        onClick={() => onEdit(plan)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50"
        aria-label={`Editar ${plan.name}`}
        title="Editar"
      >
        <Pencil className="h-4 w-4" />
      </button>
      {plan.is_active ? (
        <button
          onClick={() => onDeactivate(plan)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-100 text-red-600 transition-colors hover:bg-red-50"
          aria-label={`Desativar ${plan.name}`}
          title="Desativar"
        >
          <PowerOff className="h-4 w-4" />
        </button>
      ) : (
        <button
          onClick={() => onReactivate(plan)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-green-100 text-green-600 transition-colors hover:bg-green-50"
          aria-label={`Reativar ${plan.name}`}
          title="Reativar"
        >
          <Power className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: LucideIcon
  label: string
  value: string
  detail: string
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
        <Icon className="h-4 w-4" />
      </div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900">{value}</p>
      <p className="mt-1 text-sm text-gray-500">{detail}</p>
    </div>
  )
}

function HeaderCell({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <th className={`px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 ${className}`}>
      {children}
    </th>
  )
}

function LimitValue({ value, unlimited }: { value: number | null; unlimited?: boolean }) {
  return <span className="text-sm font-medium text-gray-900">{formatLimit(value, unlimited)}</span>
}

function formatLimit(value: number | null, unlimited?: boolean) {
  if (unlimited || value === null) return 'Sob contrato'
  return value.toLocaleString('pt-BR')
}

function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
      Ativo
    </span>
  ) : (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
      Inativo
    </span>
  )
}

function formatPrice(cents: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format((cents || 0) / 100)
}
